// src/features/editor/components/course-settings/MascotSlot.tsx
import { MascotMedia } from '@/components/common/MascotMedia'
import { useRef } from 'react'
import { RefreshCw, Trash2, Upload } from 'lucide-react'
import { cn, Dropzone } from '@teach/ui'
import { useFileUpload } from '../shared/useFileUpload'
import { UploadProgressOverlay } from '../shared/UploadProgressOverlay'
import { MASCOT_ACCEPT_ATTR, uploadCompressionFor } from '../../lib/imageFormats'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  image: string | null
  /** Нет прав или курс на проверке: ничего делать нельзя, в том числе удалять. */
  disabled: boolean
  /** Загрузка закрыта (мало уроков, не заполнено предыдущее место): заменить и загрузить
   *  нельзя, но УДАЛИТЬ уже стоящего маскота можно всегда. */
  uploadLocked?: boolean
  disabledHint?: string
  /** Заголовок пустого места (по умолчанию «Маскот»): у закрытого по числу уроков — «С 6 уроков». */
  emptyTitle?: string
  uploadLabel: string
  replaceLabel: string
  removeLabel: string
  /** Имя скрытого file-input: у него и у видимой кнопки «заменить» не должно быть одного
   *  и того же доступного имени, иначе скринридер объявляет его дважды. */
  fileLabel: string
  /** Резолвится успехом PUT — до него старый блоб трогать нельзя. */
  onUploaded: (image: string) => Promise<boolean>
  onRemove: () => Promise<boolean>
}

// Слот маскота на карте курса (RoadmapPreview): стоит в колонке маскотов там же, где
// плеер рисует персонажей. Пустой — Dropzone (onMedia) для
// загрузки, заполненный — картинка с наводимыми кнопками «заменить»/«удалить». Позиция —
// presentational only: у маскота нет поля позиции (src/types/course.ts), студия её не
// контролирует, рантайм расставляет персонажей сам. Загрузка/удаление блоба — через общий
// useFileUpload (тот же хук, что у ImageUpload), поэтому старый файл всегда чистится.
export const MascotSlot = ({
  image,
  disabled,
  uploadLocked = false,
  disabledHint,
  emptyTitle,
  uploadLabel,
  replaceLabel,
  removeLabel,
  fileLabel,
  onUploaded,
  onRemove,
}: Props) => {
  const m = useMessages(messages)
  const { progress, upload, removeFile } = useFileUpload({ compressionType: 'banner_compression' })
  const uploading = progress !== null
  const inputRef = useRef<HTMLInputElement>(null)

  // Блоб удаляем ТОЛЬКО после успешного PUT: упавшее сохранение (403/сеть) оставляет курс
  // ссылаться на старую картинку, и удалённый заранее файл превратил бы её в битую ссылку.
  const handleFile = async (file: File) => {
    const url = await upload(file, { compressionType: uploadCompressionFor(file) })
    if (!url) return
    const previous = image
    const saved = await onUploaded(url)
    if (previous && saved) removeFile(previous)
  }

  const handleRemove = async () => {
    const previous = image
    const saved = await onRemove()
    if (previous && saved) removeFile(previous)
  }

  return (
    <div className="relative aspect-square w-[6.875rem] rounded-2xl">
      {/* Загрузка — тот же вид, что у всех картинок студии; карта светлая — тон холста. */}
      <UploadProgressOverlay progress={progress} tone="canvas" size="sm" />
      <input
        ref={inputRef}
        type="file"
        accept={MASCOT_ACCEPT_ATTR}
        className="hidden"
        disabled={disabled || uploadLocked}
        aria-label={fileLabel}
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      {image ? (
        <div className="relative h-full w-full">
          <MascotMedia src={image} className="h-full w-full object-contain drop-shadow-lg" />
          {/* «Заменить» и «Удалить» видны всегда, а не только по наведению: удаление
              маскота должно находиться без поиска. Кнопки светлые — они лежат на
              светлой карте (палитра карты плеера, а не темы студии). */}
          {disabled || uploading ? null : (
            <div className="absolute -right-2 -top-2 flex gap-1.5">
              {uploadLocked ? null : (
                <button
                  type="button"
                  aria-label={replaceLabel}
                  title={m.replaceMascot}
                  onClick={() => inputRef.current?.click()}
                  className="rounded-full bg-white p-2 text-[#460466] shadow-md transition-transform hover:scale-110"
                >
                  <RefreshCw className="size-4" />
                </button>
              )}
              <button
                type="button"
                aria-label={removeLabel}
                title={m.removeMascot}
                onClick={() => void handleRemove()}
                className="rounded-full bg-white p-2 text-destructive shadow-md transition-transform hover:scale-110"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="h-full overflow-hidden rounded-2xl" title={disabledHint}>
          <Dropzone
            variant="onMedia"
            icon={Upload}
            aria-label={uploadLabel}
            title={emptyTitle ?? m.mascot}
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploadLocked}
            className={cn(
              'h-full !p-2',
              disabled || uploadLocked ? 'opacity-40' : null,
              // Под оверлеем загрузки подпись зоны просвечивала бы сквозь него.
              uploading ? '[&>*]:invisible' : null,
            )}
          />
        </div>
      )}
    </div>
  )
}
